import { Card } from '../../../ui';
import React, { FunctionComponent, SyntheticEvent, useEffect, useState } from 'react';
import { Button, Form, Spinner } from '../../../ui';
import { Helmet } from 'react-helmet';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { applyActivatedSession } from '../../../../hooks/useAuth';
import { useSession } from '../../../../state/app-state';
import { useServiceErrorHandler } from '../../../../hooks/useServiceErrorHandler';
import * as auth from '../../../../services/authentication';
import { AccountActivation } from '../../../../services/models/account-activation';

const ActivateAccountPage: FunctionComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const handleServiceError = useServiceErrorHandler();

  const session = useSession();

  const [code, setCode] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [wrongPasswordLength, setWrongPasswordLength] = useState(false);

  const [accountActivation, setAccountActivation] = useState<AccountActivation>();
  const [isFetching, setIsFetching] = useState<boolean>();
  const [isActivating, setIsActivating] = useState(false);
  const [isActivated, setIsActivated] = useState(false);

  useEffect(() => {
    setWrongPasswordLength(password.length < 6 || password.length > 32);
  }, [password, setWrongPasswordLength]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const verificationCode = searchParams.get('c');

    if (!verificationCode) {
      history.push('/');
    } else {
      setCode(verificationCode);
    }
  }, [location.search, history, setCode]);

  useEffect(() => {
    if ((session?.account && session?.token) || isActivated) {
      history.push('/');
    }
  }, [session, isActivated, history]);

  useEffect(() => {
    if (!code) {
      return;
    }

    let cancelled = false;
    setIsFetching(true);
    auth
      .getAccountActivation(code)
      .then(activation => {
        if (!cancelled) setAccountActivation(activation);
      })
      .catch(error => handleServiceError(error))
      .finally(() => {
        if (!cancelled) setIsFetching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [code, handleServiceError]);

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!code || !password) {
      return;
    }

    setIsActivating(true);
    try {
      const session = await auth.activateAccount(code, password);
      // Applying the fresh session triggers the redirect effect above.
      applyActivatedSession(session);
      setIsActivated(true);
    } catch (error) {
      handleServiceError(error, () => setIsActivating(false));
    }
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordTouched(true);
    setPassword(event.target.value);
  };

  if (isFetching === undefined || isFetching) {
    return (
      <div>
        <Card>
          <Card.Body>
            <h1 className="flex items-center justify-center gap-2 text-lg font-medium text-gray-500 my-4">
              <Spinner animation="grow"></Spinner>
              <span>Loading...</span>
            </h1>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (isFetching === false && !accountActivation?.email) {
    return (
      <div>
        <Card>
          <Card.Body>
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-6">
              Oops! Something went wrong.
            </h1>
            <Link to="/">Return to Newslit</Link>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>Activate Account</title>
      </Helmet>

      <Card>
        <Card.Body>
          <h1 className="text-2xl font-bold text-gray-900 text-center mb-6">Choose Password</h1>
          <Form noValidate onSubmit={e => handleSubmit(e)}>
            {/* Read-only value, not a control — plain markup so no label points at a missing input. */}
            <div className="mb-4">
              <div className="block font-bold mb-1">Email</div>
              <div>{accountActivation?.email}</div>
            </div>

            <Form.Group controlId="password">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                isInvalid={passwordTouched && wrongPasswordLength}
                value={password}
                onChange={(e: any) => handlePasswordChange(e)}
                placeholder="At least 6 characters"
                required
              />
              {(() => {
                if (passwordTouched && wrongPasswordLength) {
                  return (
                    <Form.Text>Please create a password between 6 and 32 characters long</Form.Text>
                  );
                }
              })()}
            </Form.Group>

            <Button
              disabled={
                isActivating || !accountActivation?.email || !passwordTouched || wrongPasswordLength
              }
              className="mt-4 w-full flex"
              variant="purple"
              type="submit"
            >
              {isActivating ? 'Activating...' : 'Save Password'}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ActivateAccountPage;
